import type { QueryClient } from '@tanstack/react-query';
import { adminConsoleAuthApi } from '../api/adminConsole';
import { orchestratorAuthApi, ORCHESTRATOR_AUTH_ME_QUERY_KEY } from '../api/orchestratorAuth';
import { ADMIN_CONSOLE_ME_QUERY_KEY, isOrchestratorDisabled } from './adminSession';
import { clearMfaStepUpFailure, orchestratorSessionIsMfa } from './adminMfaStepUp';

export type CompleteOperatorLoginInput = {
  email: string;
  password: string;
  displayName?: string;
  name?: string;
};

/**
 * RBAC Phase 2: when the orchestrator session was completed with MFA, mint an attestation so the
 * admin-api token carries MFA claims. Any failure (401 not MFA, 503 secret unset, network) means
 * logging in without it; Layout then shows the MFA banner if the user holds a critical key.
 */
async function mfaAttestationForSession(): Promise<string | undefined> {
  if (isOrchestratorDisabled()) return undefined;
  try {
    const session = await orchestratorAuthApi.me();
    if (!orchestratorSessionIsMfa(session)) return undefined;
    const { attestation } = await orchestratorAuthApi.consoleMfaAttestation();
    return attestation;
  } catch {
    return undefined;
  }
}

/**
 * Ensures admin_users exists for a verified auth-server operator, then refreshes session queries.
 * Provision is create-only for new rows; it must not reset passwords for existing users (G-I11-04).
 */
export async function completeOperatorLogin(
  qc: QueryClient,
  input: CompleteOperatorLoginInput,
): Promise<void> {
  const mfaAttestation = await mfaAttestationForSession();
  await adminConsoleAuthApi.ensureOperatorAccount({
    email: input.email.trim(),
    password: input.password,
    displayName: input.displayName?.trim() || undefined,
    name: input.name?.trim() || input.displayName?.trim() || undefined,
    ...(mfaAttestation ? { mfaAttestation } : {}),
  });
  clearMfaStepUpFailure();
  await qc.invalidateQueries({ queryKey: ORCHESTRATOR_AUTH_ME_QUERY_KEY });
  await qc.invalidateQueries({ queryKey: ADMIN_CONSOLE_ME_QUERY_KEY });
}
